import { useEffect, useRef, useState } from "react";
import { Plus, Check, X, Banknote, Download, ScanText, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCollection } from "@/hooks/useCollection";
import { useAuth } from "@/context/AuthContext";
import { inr, exportCsv, api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { VENDOR_CATEGORIES, INTERNAL_CATEGORIES, BILL_STATUS, APPROVAL_STATES, PAYMENT_STATES } from "@/constants/crm";
import { Panel, StatusBadge, StatCard } from "./common";
import { DataTable } from "./DataTable";
import { FilterBar, applyFilters } from "./FilterBar";
import { FormDialog } from "./FormDialog";
import { FileBox } from "./Files";
import { PaymentBatches } from "./PaymentBatches";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export const amt = (e) => Number(e.amount || 0) + Number(e.gst_amount || 0);
const sum = (list) => list.reduce((s, e) => s + amt(e), 0);
const today = () => new Date().toISOString().slice(0, 10);
const ACCEPT = "image/*,application/pdf";
const daysSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
const missingBill = (e, counts) => e.bill_status === "Pending submission" && !counts[e.id] && e.created_at && daysSince(e.created_at) > 7;

function DuplicateDialog({ dup, onCancel, onContinue }) {
  const list = dup?.data.duplicates || [];
  return (
    <AlertDialog open={!!dup} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="duplicate-bill-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-rose-600"><AlertTriangle className="h-5 w-5" />Possible duplicate bill</AlertDialogTitle>
          <AlertDialogDescription>Bill #{dup?.data.bill_number} from {dup?.data.company || dup?.data.vendor_name} is already recorded. Check before adding it again so it isn't paid twice.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          {list.map((d) => (
            <div key={d.id} data-testid={`duplicate-bill-${d.id}`} className="flex items-center justify-between rounded-lg border border-rose-100 bg-rose-50/60 px-3 py-2 text-sm">
              <div><div className="font-semibold text-zinc-900">{d.project_name || "—"}</div><div className="text-xs text-zinc-500">{d.company} · #{d.bill_number}{d.submission_date ? ` · ${d.submission_date}` : ""}</div></div>
              <div className="text-right"><div className="font-semibold">{inr(amt(d))}</div><div className="mt-0.5 flex justify-end gap-1"><StatusBadge value={d.approval_status} /><StatusBadge value={d.payment_status} /></div></div>
            </div>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="duplicate-bill-cancel">Don't add</AlertDialogCancel>
          <AlertDialogAction data-testid="duplicate-bill-continue" className="bg-rose-600 hover:bg-rose-700" onClick={onContinue}>Add anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ScanInvoiceButton({ onRead }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (ev) => {
    const file = ev.target.files[0];
    ev.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ai/extract-invoice", fd);
      toast.success("Invoice read — please check the details before saving");
      onRead(data, file);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the invoice");
      onRead({}, file);
    } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="scan-invoice-input" onChange={pick} />
      <Button data-testid="scan-invoice-button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current.click()}>
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ScanText className="h-3.5 w-3.5 mr-1" />}{busy ? "Reading invoice…" : "Scan invoice"}
      </Button>
    </>
  );
}

async function uploadAll(files, entityType, entityId) {
  for (const file of files) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("category", "");
    await api.post("/files", fd);
  }
}

export function totals(vendor, internal, budget = 0) {
  const all = [...vendor, ...internal];
  const total = sum(all);
  return {
    vendor: sum(vendor), internal: sum(internal), total, remaining: Number(budget || 0) - total,
    submitted: sum(vendor.filter((e) => e.bill_status === "Bill submitted")),
    pendingApproval: sum(all.filter((e) => (e.approval_status || "Pending") === "Pending")),
    approved: sum(all.filter((e) => e.approval_status === "Approved")),
    rejected: sum(all.filter((e) => e.approval_status === "Rejected")),
    paid: sum(all.filter((e) => e.payment_status === "Paid")),
    outstandingVendor: sum(vendor.filter((e) => e.approval_status === "Approved" && e.payment_status !== "Paid")),
    upcoming: vendor.filter((e) => e.payment_status !== "Paid" && e.approval_status !== "Rejected" && e.due_date).sort((a, b) => a.due_date.localeCompare(b.due_date)),
  };
}

const CFG = {
  vendor: {
    coll: "vendor_expenses", mod: "VendorExpenses", title: "Vendor expenses", entity: "vendor_expense",
    fields: [
      { name: "vendor_name", label: "Vendor / contact name", required: true }, { name: "company", label: "Company name", required: true },
      { name: "category", label: "Expense category", type: "select", options: VENDOR_CATEGORIES, required: true }, { name: "bill_number", label: "Bill / invoice number" },
      { name: "amount", label: "Bill amount (₹, excl. GST)", type: "number", required: true }, { name: "gst_amount", label: "GST amount (₹)", type: "number" },
      { name: "submission_date", label: "Submission date", type: "date" }, { name: "due_date", label: "Due date", type: "date" },
      { name: "bill_status", label: "Bill status", type: "select", options: BILL_STATUS, required: true }, { name: "remarks", label: "Remarks", type: "textarea" },
      { name: "bill_files", label: "Invoice / bill (image or PDF)", type: "file", accept: ACCEPT, hint: "Attach one or more JPG, PNG or PDF files." },
    ],
    defaults: { bill_status: "Pending submission", category: "Fabrication" },
    search: ["vendor_name", "company", "bill_number", "remarks", "po_number"],
  },
  internal: {
    coll: "internal_expenses", mod: "InternalExpenses", title: "Internal expenses", entity: "internal_expense",
    fields: [
      { name: "employee", label: "Employee / contact name", required: true }, { name: "company", label: "Company name", required: true },
      { name: "category", label: "Expense category", type: "select", options: INTERNAL_CATEGORIES, required: true }, { name: "amount", label: "Amount (₹)", type: "number", required: true },
      { name: "expense_date", label: "Expense date", type: "date" }, { name: "remarks", label: "Remarks / reason", type: "textarea" },
      { name: "bill_files", label: "Bill / receipt (image or PDF)", type: "file", accept: ACCEPT, hint: "Attach one or more JPG, PNG or PDF files." },
    ],
    defaults: { company: "ZDS", category: "Travel" },
    search: ["employee", "company", "category", "remarks"],
  },
};

function Actions({ e, c, update, can, user, onPay }) {
  const ap = e.approval_status || "Pending";
  const ready = c.coll !== "vendor_expenses" || e.bill_status === "Bill submitted";
  return (
    <div className="flex flex-wrap gap-1" onClick={(ev) => ev.stopPropagation()}>
      {can(c.mod, "approve") && ap === "Pending" && ready && (
        <>
          <Button data-testid={`exp-approve-${e.id}`} size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" onClick={() => update(e.id, { approval_status: "Approved", approved_by: user.name, approved_at: today() }, "Bill approved")}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
          <Button data-testid={`exp-reject-${e.id}`} size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => update(e.id, { approval_status: "Rejected", approved_by: user.name, approved_at: today() }, "Bill rejected")}><X className="h-3.5 w-3.5 mr-1" />Reject</Button>
        </>
      )}
      {can(c.mod, "edit") && ap === "Approved" && e.payment_status !== "Paid" && (
        <Button data-testid={`exp-pay-${e.id}`} size="sm" variant="ghost" className="h-7 px-2 text-violet-700" onClick={() => onPay(e)}><Banknote className="h-3.5 w-3.5 mr-1" />Mark paid</Button>
      )}
    </div>
  );
}

export function ExpenseTable({ kind, projectId, projects }) {
  const c = CFG[kind];
  const { can, user } = useAuth();
  const { items, create, update, reload } = useCollection(c.coll, projectId ? { project_id: projectId } : {});
  const [batches, setBatches] = useState([]);
  const loadBatches = () => { if (kind === "vendor" && can("Budget", "view")) api.get("/payment-batches").then((r) => setBatches(r.data)).catch(() => {}); };
  useEffect(() => { loadBatches(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const [search, setSearch] = useState("");
  const [ap, setAp] = useState("");
  const [pay, setPay] = useState("");
  const [form, setForm] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [counts, setCounts] = useState({});
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [dup, setDup] = useState(null);
  const [chasing, setChasing] = useState(false);
  const [ackBill, setAckBill] = useState("");
  const openScanned = ({ data, file }) => {
    const { duplicates, ...fieldsRead } = data;
    if (duplicates?.length) setAckBill(fieldsRead.bill_number || "");
    setForm({ ...c.defaults, ...fieldsRead, ...(fieldsRead.bill_number ? { bill_status: "Bill submitted" } : {}), bill_files: [file] });
  };
  const onScanned = (data, file) => (data.duplicates?.length ? setDup({ data, file }) : openScanned({ data, file }));
  const chaseNow = async () => {
    setChasing(true);
    try {
      const { data } = await api.post("/reminders/bill-chaser/run");
      toast.success(`Reminders sent: ${data.chatted.length} chat, ${data.emailed.length} email`, { description: data.skipped.length ? `Skipped — ${data.skipped.join("; ")}` : undefined, duration: 8000 });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setChasing(false);
  };
  useEffect(() => {
    if (kind !== "vendor") return;
    api.get("/files/counts", { params: { entity_type: c.entity } }).then((r) => setCounts(r.data)).catch(() => {});
  }, [kind, c.entity, fileKey, items.length]);
  const missing = kind === "vendor" ? items.filter((e) => missingBill(e, counts)) : [];
  const filtered = applyFilters(items, search, c.search, [{ key: "approval_status", value: ap }, { key: "payment_status", value: pay }]);
  const rows = onlyMissing ? filtered.filter((e) => missingBill(e, counts)) : filtered;
  const t = totals(kind === "vendor" ? items : [], kind === "internal" ? items : []);
  const pName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const fields = projectId ? c.fields : [{ name: "project_id", label: "Project", type: "select", options: projects.map((p) => ({ value: p.id, label: p.name })), required: true, full: true }, ...c.fields];

  const nrm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const exportBatch = async () => {
    const due = items.filter((e) => e.approval_status === "Approved" && e.payment_status !== "Paid").sort((a, b) => (a.due_date || "9").localeCompare(b.due_date || "9"));
    if (!due.length) { toast.info("No approved, unpaid vendor bills to export"); return; }
    const vendors = await api.get("/vendors").then((r) => r.data).catch(() => []);
    const find = (e) => vendors.find((v) => [v.company, v.name].some((x) => nrm(x) && [e.company, e.vendor_name].some((y) => nrm(y) === nrm(x))));
    let missingBank = 0;
    const rows = due.map((e, i) => {
      const v = find(e) || {};
      if (!v.account_no || !v.ifsc) missingBank += 1;
      return { sr: i + 1, beneficiary: v.beneficiary_name || e.company, account_no: v.account_no || "", ifsc: v.ifsc || "", bank: v.bank_name || "", amount: amt(e).toFixed(2),
        narration: `${e.bill_number ? `Bill ${e.bill_number}` : "Vendor bill"} - ${e.project_name || ""}`.slice(0, 60), company: e.company, vendor_name: e.vendor_name, gstin: v.gst_no || "",
        bill_number: e.bill_number, submission_date: e.submission_date, due_date: e.due_date, base: Number(e.amount || 0).toFixed(2), gst: Number(e.gst_amount || 0).toFixed(2),
        project_name: e.project_name, category: e.category, approved_by: e.approved_by, approved_at: e.approved_at, email: v.email || "", phone: v.phone || "" };
    });
    const total = due.reduce((s, e) => s + amt(e), 0);
    let batch;
    try { batch = (await api.post("/payment-batches", { expense_ids: due.map((e) => e.id) })).data; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); return; }
    rows.forEach((r) => { r.batch = batch.id; });
    rows.push({ sr: "TOTAL", batch: batch.id, amount: total.toFixed(2) });
    loadBatches();
    exportCsv(`payment-batch-${batch.id}.csv`, rows, [["batch", "Batch ID"], ["sr", "Sr"], ["beneficiary", "Beneficiary name"], ["account_no", "Account number"], ["ifsc", "IFSC"], ["bank", "Bank"], ["amount", "Payable amount (₹)"], ["narration", "Narration"],
      ["company", "Vendor company"], ["vendor_name", "Vendor contact"], ["gstin", "GSTIN"], ["bill_number", "Bill no."], ["submission_date", "Bill date"], ["due_date", "Due date"], ["base", "Amount excl. GST"], ["gst", "GST"],
      ["project_name", "Project"], ["category", "Category"], ["approved_by", "Approved by"], ["approved_at", "Approved on"], ["email", "Vendor email"], ["phone", "Vendor phone"]].map(([key, label]) => ({ key, label })));
    toast.success(`Payment batch ${batch.id} exported: ${due.length} bill${due.length > 1 ? "s" : ""}, ${inr(total)}`,
      { description: missingBank ? `${missingBank} bill${missingBank > 1 ? "s are" : " is"} missing vendor bank details — add them in Vendor Management.` : undefined, duration: 8000 });
  };
  const sendDueAlerts = async () => {
    try {
      const { data } = await api.post("/reminders/due-alerts/run");
      toast.success(`Due alerts for ${data.due_date}: ${data.bills} bill(s), ${data.chatted.length} chat, ${data.emailed.length} email`, { description: data.skipped.length ? `Skipped — ${data.skipped.join("; ")}` : undefined, duration: 8000 });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const sendSummary = async () => {
    try {
      const { data } = await api.post("/reminders/payment-summary/run");
      if (data.emailed.length) toast.success(`Payment summary emailed: ${data.overdue} overdue, ${data.upcoming} due this week, ${data.approved_unpaid + data.internal_unpaid} approved unpaid`);
      else toast.error(`Summary not sent — ${data.skipped.join("; ")}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const save = async (f, force = false) => {
    const { bill_files: files = [], ...rest } = f;
    if (kind === "vendor" && !force && f.bill_number && f.bill_number !== ackBill) {
      const prev = f.id && items.find((e) => e.id === f.id);
      if (!prev || prev.bill_number !== f.bill_number || prev.company !== f.company) {
        const { data: hits } = await api.get("/vendor-expenses/duplicates", { params: { bill_number: f.bill_number, company: f.company || "", vendor_name: f.vendor_name || "", exclude_id: f.id || "" } });
        if (hits.length) { setDup({ data: { bill_number: f.bill_number, company: f.company, vendor_name: f.vendor_name, duplicates: hits }, pending: f }); return false; }
      }
    }
    const body = { ...rest, project_id: projectId || f.project_id, project_name: pName(projectId || f.project_id) };
    const doc = f.id
      ? await update(f.id, Object.fromEntries(Object.entries(body).filter(([k]) => !["approval_status", "approved_by", "approved_at"].includes(k))))
      : await create({ ...body, approval_status: "Pending", payment_status: "Pending", submitted_by: user.name }, "Expense added");
    if (!doc) return false;
    if (files.length) {
      try { await uploadAll(files, c.entity, doc.id); toast.success(`${files.length} file(s) attached`); setFileKey((k) => k + 1); }
      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    }
    return true;
  };

  const cols = [
    ...(projectId ? [] : [{ key: "project_name", label: "Project" }]),
    kind === "vendor"
      ? { key: "vendor_name", label: "Vendor / bill", render: (e) => <div><div className="font-semibold text-zinc-900">{e.company}</div><div className="text-xs text-zinc-500">{e.vendor_name}{e.bill_number ? ` · #${e.bill_number}` : ""}</div>{missingBill(e, counts) && <div data-testid={`missing-bill-flag-${e.id}`} className="mt-1 inline-flex items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600"><AlertTriangle className="h-3 w-3" />No bill · {daysSince(e.created_at)} days</div>}</div> }
      : { key: "employee", label: "Employee", render: (e) => <div><div className="font-semibold text-zinc-900">{e.employee}</div><div className="text-xs text-zinc-500">{e.company} · by {e.submitted_by}</div></div> },
    { key: "category", label: "Category" },
    { key: "amount", label: "Amount", render: (e) => <div className="whitespace-nowrap"><div className="font-semibold text-zinc-900">{inr(amt(e))}</div>{kind === "vendor" && Number(e.gst_amount) > 0 && <div className="text-[11px] text-zinc-400">incl. GST {inr(e.gst_amount)}</div>}</div> },
    kind === "vendor"
      ? { key: "dates", label: "Submitted / due", render: (e) => <div className="whitespace-nowrap text-xs"><div>{e.submission_date || "—"}</div><div className={e.due_date && e.due_date < today() && e.payment_status !== "Paid" ? "text-rose-500" : "text-zinc-400"}>due {e.due_date || "—"}</div></div> }
      : { key: "expense_date", label: "Date" },
    ...(kind === "vendor" ? [{ key: "bill_status", label: "Bill", render: (e) => <StatusBadge value={e.bill_status} /> }] : []),
    { key: "approval_status", label: "Approval", render: (e) => <div><StatusBadge testid={`exp-approval-${e.id}`} value={e.approval_status} />{e.approved_by && <div className="mt-0.5 text-[10px] text-zinc-400">by {e.approved_by}</div>}</div> },
    { key: "payment_status", label: "Payment", render: (e) => <div><StatusBadge testid={`exp-payment-${e.id}`} value={e.payment_status} />{e.payment_date && <div className="mt-0.5 text-[10px] text-zinc-400">{e.payment_date}{e.payment_reference ? ` · ${e.payment_reference}` : ""}</div>}{e.receipt_status && <div data-testid={`exp-receipt-${e.id}`} className="mt-0.5 max-w-[150px] truncate text-[10px] text-zinc-400" title={e.receipt_status}>{e.receipt_status}</div>}</div> },
    { key: "files", label: kind === "vendor" ? "Invoice" : "Receipt", render: (e) => <div onClick={(ev) => ev.stopPropagation()} className="max-w-[170px]"><FileBox compact key={`${e.id}-${fileKey}`} accept={ACCEPT} testid={`exp-files-${e.id}`} entityType={c.entity} entityId={e.id} canUpload={can(c.mod, "edit") || can(c.mod, "create")} /></div> },
    { key: "act", label: "", render: (e) => <Actions e={e} c={c} update={update} can={can} user={user} onPay={setPayFor} /> },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid={`${kind}-exp-total`} label={`Total ${kind} expenses`} value={inr(t.total)} hint={`${items.length} entries`} />
        <StatCard testid={`${kind}-exp-pending`} label="Pending approval" value={inr(t.pendingApproval)} tone="text-amber-600" />
        <StatCard testid={`${kind}-exp-approved-unpaid`} label="Approved, unpaid" value={inr(t.approved - t.paid)} tone="text-violet-700" />
        <StatCard testid={`${kind}-exp-paid`} label="Paid" value={inr(t.paid)} tone="text-emerald-600" />
      </div>
      <Panel title={c.title} action={
        <div className="flex flex-wrap justify-end gap-2">
          <Button data-testid={`${kind}-exp-export-button`} size="sm" variant="outline" onClick={() => exportCsv(`${c.coll}.csv`, rows.map((e) => ({ ...e, total: amt(e) })), [...(projectId ? [] : [{ key: "project_name", label: "Project" }]), ...c.fields.filter((f) => f.type !== "file").map((f) => ({ key: f.name, label: f.label })), { key: "total", label: "Total" }, { key: "approval_status", label: "Approval" }, { key: "payment_status", label: "Payment" }, { key: "payment_date", label: "Payment date" }, { key: "payment_reference", label: "Payment ref" }])}><Download className="h-3.5 w-3.5 mr-1" />Export</Button>
          {kind === "vendor" && can("Budget", "view") && <Button data-testid="payment-batch-export-button" size="sm" variant="outline" onClick={exportBatch}><Banknote className="h-3.5 w-3.5 mr-1" />Payment batch</Button>}
          {kind === "vendor" && user.role === "admin" && <Button data-testid="due-alerts-send-now" size="sm" variant="outline" onClick={sendDueAlerts}>Send due alerts</Button>}
          {kind === "vendor" && user.role === "admin" && <Button data-testid="payment-summary-send-now" size="sm" variant="outline" onClick={sendSummary}>Email payment summary</Button>}
          {kind === "vendor" && can(c.mod, "create") && <ScanInvoiceButton onRead={onScanned} />}
          {can(c.mod, "create") && <Button data-testid={`${kind}-exp-add-button`} size="sm" onClick={() => setForm({ ...c.defaults, ...(kind === "internal" ? { employee: user.name } : {}) })}><Plus className="h-3.5 w-3.5 mr-1" />Add expense</Button>}
        </div>}>
        {missing.length > 0 && (
          <div data-testid="missing-bill-alert" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /><b>{missing.length}</b> vendor expense{missing.length > 1 ? "s are" : " is"} still “Pending submission” with no invoice for over 7 days ({inr(sum(missing))}).</span>
            <div className="flex gap-1">
              {user.role === "admin" && <Button data-testid="bill-chaser-send-now" size="sm" variant="ghost" className="h-7 text-rose-700" disabled={chasing} onClick={chaseNow}>{chasing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}Send reminders now</Button>}
              <Button data-testid="missing-bill-filter-toggle" size="sm" variant="ghost" className="h-7 text-rose-700" onClick={() => setOnlyMissing((v) => !v)}>{onlyMissing ? "Show all" : "Show only these"}</Button>
            </div>
          </div>
        )}
        <FilterBar testid={`${kind}-exp`} search={search} setSearch={setSearch} filters={[
          { key: "approval", label: "approval states", value: ap, set: setAp, options: APPROVAL_STATES },
          { key: "payment", label: "payment states", value: pay, set: setPay, options: PAYMENT_STATES },
        ]} />
        <DataTable testid={`${kind}-exp-table`} rows={rows} onRowClick={(e) => can(c.mod, "edit") && e.payment_status !== "Paid" && setForm(e)} columns={cols} />
        <p className="mt-3 text-xs text-zinc-400">Bill approval and payment are tracked separately — an approved bill stays unpaid until payment is recorded.</p>
      </Panel>
      {kind === "vendor" && <PaymentBatches batches={batches} canPay={can(c.mod, "edit")} onPaid={() => { reload(); loadBatches(); setTimeout(reload, 4000); }} />}
      <DuplicateDialog dup={dup} onCancel={() => setDup(null)} onContinue={async () => {
        const d = dup; setDup(null);
        if (d.pending) { if (await save(d.pending, true)) { setForm(null); setAckBill(""); } } else openScanned(d);
      }} />
      <FormDialog testid={`${kind}-exp-form`} open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Edit expense" : `Add ${kind} expense`} fields={fields} initial={form} onSubmit={save} />
      <FormDialog testid={`${kind}-exp-pay-form`} open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)} title="Record payment" submitLabel="Mark as paid" initial={{ payment_date: today() }}
        fields={[{ name: "payment_date", label: "Payment date", type: "date", required: true }, { name: "payment_reference", label: "Payment reference (UTR / cheque no.)" }]}
        onSubmit={async (f) => { const ok = !!(await update(payFor.id, { ...f, payment_status: "Paid" }, "Payment recorded")); if (ok && kind === "vendor") setTimeout(reload, 4000); return ok; }} />
    </div>
  );
}

export function FinancialSummary({ project, vendor, internal }) {
  const t = totals(vendor, internal, project?.budget);
  return (
    <div className="space-y-4" data-testid="financial-summary">
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard testid="fin-vendor" label="Vendor expenses" value={inr(t.vendor)} />
        <StatCard testid="fin-internal" label="Internal expenses" value={inr(t.internal)} />
        <StatCard testid="fin-total" label="Total expenses" value={inr(t.total)} hint={project ? `of ${inr(project.budget)} budget` : undefined} />
        <StatCard testid="fin-pending" label="Pending bills" value={inr(t.pendingApproval)} tone="text-amber-600" />
        {project && <StatCard testid="fin-remaining" label="Remaining budget" value={inr(t.remaining)} tone={t.remaining < 0 ? "text-rose-600" : "text-emerald-600"} />}
        <StatCard testid="fin-approved" label="Approved bills" value={inr(t.approved)} />
        <StatCard testid="fin-outstanding" label="Outstanding vendor payments" value={inr(t.outstandingVendor)} tone="text-violet-700" />
        <StatCard testid="fin-paid" label="Paid" value={inr(t.paid)} tone="text-emerald-600" hint={`Rejected ${inr(t.rejected)}`} />
      </div>
      <Panel testid="upcoming-dues-panel" title="Upcoming vendor due dates">
        {t.upcoming.length ? t.upcoming.slice(0, 6).map((e) => (
          <div key={e.id} className="flex items-center justify-between border-b border-zinc-100 py-2.5 text-sm last:border-0">
            <div><div className="font-medium text-zinc-800">{e.company}{!project && <span className="text-zinc-400"> · {e.project_name}</span>}</div><div className="text-xs text-zinc-500">{e.category} · <StatusBadge value={e.approval_status} /></div></div>
            <div className="text-right"><div className="font-semibold text-zinc-900">{inr(amt(e))}</div><div className={`text-xs ${e.due_date < today() ? "text-rose-500" : "text-zinc-400"}`}>due {e.due_date}</div></div>
          </div>
        )) : <div className="py-6 text-center text-sm text-zinc-400">No unpaid vendor bills with due dates.</div>}
      </Panel>
    </div>
  );
}
